import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { messagesApi } from "./api";
import type { SendMessageRequest } from "@/features/tasks/types";

export function useMessages() {
  return useInfiniteQuery({
    queryKey: ["messages"],
    queryFn: ({ pageParam }) => messagesApi.getMessages(pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    select: (data) => ({
      ...data,
      pages: data.pages.slice().reverse(), // Reverse for chronological order
    }),
  });
}

export function useSendGeneralMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SendMessageRequest) => messagesApi.sendMessage(data),
    onMutate: async (data) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ["messages"],
      });

      // Optimistic update
      const optimisticMessage = {
        id: Date.now(), // Temporary ID
        taskId: null,
        body: data.body,
        direction: "incoming" as const,
        createdAt: new Date().toISOString(),
        senderName: "You",
      };

      queryClient.setQueryData(
        ["messages"],
        (old: any) => {
          if (!old) return old;
          
          return {
            ...old,
            pages: old.pages.map((page: any, index: number) => {
              if (index === old.pages.length - 1) {
                return {
                  ...page,
                  data: [...page.data, optimisticMessage],
                };
              }
              return page;
            }),
          };
        }
      );

      return { optimisticMessage };
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update
      if (context?.optimisticMessage) {
        queryClient.setQueryData(
          ["messages"],
          (old: any) => {
            if (!old) return old;
            
            return {
              ...old,
              pages: old.pages.map((page: any, index: number) => {
                if (index === old.pages.length - 1) {
                  return {
                    ...page,
                    data: page.data.filter(
                      (msg: any) => msg.id !== context.optimisticMessage.id
                    ),
                  };
                }
                return page;
              }),
            };
          }
        );
      }
    },
    onSettled: () => {
      // Refetch messages to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ["messages"] });
    },
  });
}
